import { Box, Typography } from '@mui/material';
import { ReactNode } from 'react';
import { PartnerTimeZoneTooltip, SidePanelCard } from 'src/components';
import { PartnerBusinessStatusLine } from './partner-business-status-line';
import { usePartnerLocalTime } from './use-partner-local-time';

const PARTNER_FALLBACK_NAME = 'Partner';
const PARTNER_FALLBACK_INITIALS = '—';

const partnerTimeZoneTooltipProps = {
  placement: 'bottom-start' as const,
};

const getPartnerInitials = (businessName?: string | null) => {
  const words = (businessName ?? '').trim().split(/\s+/).filter(Boolean);

  if (!words.length) {
    return PARTNER_FALLBACK_INITIALS;
  }

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
};

interface PartnerCardPartner {
  businessName?: string | null;
  partnerId?: number | null;
  timeZone?: string | null;
}

interface PartnerCardProps {
  partner?: PartnerCardPartner | null;
  'data-testid'?: string;
  children?: ReactNode;
}

export const PartnerCard = ({
  partner,
  'data-testid': dataTestId = 'partner-card',
  children,
}: PartnerCardProps) => {
  const { formattedDate, formattedTime } = usePartnerLocalTime(partner?.timeZone ?? undefined);

  return (
    <SidePanelCard
      data-testid={dataTestId}
      defaultOpen={false}
      mountContentOnOpen
      leading={
        <Box
          aria-hidden
          sx={(theme) => ({
            flex: 'none',
            width: '1.875rem',
            height: '1.875rem',
            borderRadius: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            fontWeight: 700,
            backgroundColor: theme.palette.custom.OV.sys.primaryContainer,
            color: theme.palette.custom.OV.sys.onPrimaryContainer,
          })}
        >
          {getPartnerInitials(partner?.businessName)}
        </Box>
      }
      subtitle={
        <>
          <PartnerTimeZoneTooltip
            date={new Date().toISOString()}
            partnerId={partner?.partnerId}
            tooltipProps={partnerTimeZoneTooltipProps}
          >
            <Typography component="span" noWrap sx={{ display: 'block', fontSize: 'inherit' }}>
              {formattedDate} · {formattedTime}
            </Typography>
          </PartnerTimeZoneTooltip>
          <PartnerBusinessStatusLine
            partnerId={partner?.partnerId}
            timeZone={partner?.timeZone}
          />
        </>
      }
      title={partner?.businessName || PARTNER_FALLBACK_NAME}
    >
      {children}
    </SidePanelCard>
  );
};
